/**
 * Spend classification — the one predicate for "is this discretionary spend".
 *
 * Money movement that is not discretionary spending: income, transfers between
 * own accounts, loan disbursements, debt and loan payments (credit cards,
 * student loans, mortgages, auto, cash advances), rent and utilities, and bank
 * fees. These happen regardless of what a person chooses to buy, so every
 * "usual week" / "money kept" number excludes them. Pure module — no Firebase.
 *
 * Used by the Found screen (`services/found/foundMath.ts`) and Quiet Days
 * savings (`apps/enough/src/services/quietDaysSavings.ts`), which previously
 * carried its own copy of the list.
 */

/** PFC primary groups that are never discretionary spend. */
export const NON_SPEND_PRIMARY_CATEGORIES = [
  'INCOME',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'LOAN_DISBURSEMENTS',
  'LOAN_PAYMENTS',
  'RENT_AND_UTILITIES',
  'BANK_FEES',
] as const;

/** Detailed codes that are never discretionary spend (kept for readers that only carry `categoryDetailed`). */
export const NON_SPEND_DETAILED_CATEGORIES = [
  'LOAN_PAYMENTS_CREDIT_CARD_PAYMENT',
  'TRANSFER_IN_ACCOUNT_TRANSFER',
  'TRANSFER_OUT_ACCOUNT_TRANSFER',
] as const;

const PRIMARY = new Set<string>(NON_SPEND_PRIMARY_CATEGORIES);
const DETAILED = new Set<string>(NON_SPEND_DETAILED_CATEGORIES);

export interface SpendClassifiable {
  category?: string;
  categoryDetailed?: string;
  amount?: number;
  pending?: boolean;
}

/**
 * True when the transaction is money movement rather than spending: income, a
 * transfer, a loan payment or disbursement, rent/utilities, or a bank fee.
 * Reads the primary group, the detailed code, and the detailed code's prefix
 * (so an unknown sub-code of a non-spend group is still caught).
 */
export function isNonSpendTransaction(tx: SpendClassifiable): boolean {
  if (tx.category && PRIMARY.has(tx.category)) return true;
  const detailed = tx.categoryDetailed;
  if (!detailed) return false;
  if (DETAILED.has(detailed)) return true;
  for (const p of PRIMARY) if (detailed.startsWith(p + '_')) return true;
  return false;
}

/**
 * True for a posted outflow that a person chose: `amount > 0` (Plaid outflows
 * are positive), not pending, and not classified as non-spend.
 */
export function isDiscretionarySpend(tx: SpendClassifiable): boolean {
  if (!(typeof tx.amount === 'number' && tx.amount > 0)) return false;
  if (tx.pending) return false;
  return !isNonSpendTransaction(tx);
}
